import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  MenuItem,
} from "@mui/material";
import {
  getShiftTitles,
  createShiftTitle,
} from "../../../../services/Settings/ServicesandTaxSettingsAndSessios/SessoinAndShiftsApi";
import {
  fetchServiceAndTaxSettings,
  saveServiceAndTaxSettingsAndSessions,
} from "../../../../services/Settings/ServicesandTaxSettingsAndSessios/ServicesAndTaxApi";
import SessionSettings from "./SessionSettings";

const Shiftname = () => {
  const [shiftTitles, setShiftTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [newShiftName, setNewShiftName] = useState("");
  const [addingShift, setAddingShift] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [settings, setSettings] = useState(null);
  const [shift, setShift] = useState(""); // حالة الشيفت
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [ctrlPPrint, setCtrlPPrint] = useState(false);
  const [autoPreparationPrint, setAutoPreparationPrint] = useState(false);
  const [invoiceIncrement, setInvoiceIncrement] = useState(""); // اجعلها فارغة افتراضياً

  const shiftNames = {
    optional: "اختياري",
    required: "إجباري",
  };

  useEffect(() => {
    const fetchShiftTitles = async () => {
      try {
        const rootId = localStorage.getItem("branch_id");
        if (!rootId) throw new Error("❌ لم يتم العثور على branch_id.");

        const response = await getShiftTitles(rootId);

        // ✅ خزن الـ shiftTitles مباشرة
        setShiftTitles(response);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchShiftTitles();
  }, []);

  useEffect(() => {
    fetchServiceAndTaxSettings()
      .then((data) => {
        setSettings(data);
        setShift(data.shift || "");
        setCtrlPPrint(Boolean(data.ctrl_p_print));
        setAutoPreparationPrint(Boolean(data.auto_preparation_print));
        setInvoiceIncrement(data.invoice_incremant || ""); // اجلبها كما هي من البيانات
      })
      .catch(() => {
        setSettings(null);
        setCtrlPPrint(false);
        setAutoPreparationPrint(false);
        setInvoiceIncrement(""); // في حال الخطأ اجعلها فارغة
      })
      .finally(() => setLoading(false));
  }, []);

  const handleAddShift = async () => {
    const rootId = localStorage.getItem("branch_id");
    if (!rootId) {
      alert("❌ لم يتم العثور على branch_id.");
      return;
    }

    if (!newShiftName.trim()) {
      alert("❌ يرجى إدخال اسم الشيفت.");
      return;
    }

    setAddingShift(true);
    try {
      const newShift = await createShiftTitle(rootId, newShiftName);
      setShiftTitles((prev) => [...prev, newShift]);
      setNewShiftName("");
      setOpenModal(false);
      alert("✅ تم إضافة الشيفت بنجاح.");
    } catch (err) {
      alert(`❌ فشل في إضافة الشيفت: ${err.message}`);
    } finally {
      setAddingShift(false);
    }
  };

  const handleShiftSave = async () => {
    setConfirmOpen(false);
    setSaving(true);
    setSuccessMsg("");
    // تحقق من حالة اليومية قبل الحفظ
    if (settings?.session === "automatic" && shift === "required") {
      setAlertOpen(true);
      setSaving(false);
      return;
    }
    try {
      await saveServiceAndTaxSettingsAndSessions({
        session: settings?.session ?? "",
        shift,
        tax: settings?.tax ?? "",
        service: settings?.service ?? "",
        delivery: settings?.delivery ?? "",
        tax_is_percentage: Boolean(settings?.tax_is_percentage),
        service_is_percentage: Boolean(settings?.service_is_percentage),
        delivery_is_percentage: Boolean(settings?.delivery_is_percentage),
        is_service_required: Boolean(settings?.is_service_required),
        is_delivery_required: Boolean(settings?.is_delivery_required),
        is_tax_required: Boolean(settings?.is_tax_required),
        ctrl_p_print: ctrlPPrint ? 1 : 0,
        auto_preparation_print: autoPreparationPrint ? 1 : 0,
        invoice_incremant: invoiceIncrement, // أرسلها كما هي
      });
      setSuccessMsg("✅ تم حفظ حالة الشيفت بنجاح!");
      fetchServiceAndTaxSettings().then((data) => {
        setSettings(data);
        setShift(data.shift || "");
        setCtrlPPrint(Boolean(data.ctrl_p_print));
        setAutoPreparationPrint(Boolean(data.auto_preparation_print));
        setInvoiceIncrement(data.invoice_incremant || "");
      });
    } catch (err) {
      setSuccessMsg("❌ فشل في حفظ حالة الشيفت.");
    } finally {
      setSaving(false);
      setTimeout(() => setSuccessMsg(""), 3000);
    }
  };

  if (loading)
    return <CircularProgress sx={{ display: "block", mx: "auto", mt: 4 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 2 }}>
        <Tab label="اليومية" />
        <Tab label="الشيفتات" />
      </Tabs>

      {tabIndex === 0 && (
        <Box>
          {/* محتوى اليومية */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            اليومية
          </Typography>
          <SessionSettings />
        </Box>
      )}

      {tabIndex === 1 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            حالة الشيفت
          </Typography>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography>
                الحالة الحالية: {shiftNames[settings?.shift] || settings?.shift}
              </Typography>
              <TextField
                select
                label="تغيير حالة الشيفت"
                value={shift}
                onChange={(e) => setShift(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="optional">{shiftNames.optional}</MenuItem>
                <MenuItem value="required">{shiftNames.required}</MenuItem>
              </TextField>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setConfirmOpen(true)}
                disabled={saving}
              >
                {saving ? "جاري الحفظ..." : "حفظ حالة الشيفت"}
              </Button>
            </Box>
            {successMsg && (
              <Typography
                sx={{
                  mt: 2,
                  color: successMsg.startsWith("✅") ? "green" : "red",
                }}
              >
                {successMsg}
              </Typography>
            )}
          </Paper>
          <Button
            variant="contained"
            color="primary"
            sx={{ mb: 2 }}
            onClick={() => setOpenModal(true)}
          >
            إضافة شيفت جديد
          </Button>
          {shiftTitles.length > 0 ? (
            <Paper sx={{ p: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>المعرف</TableCell>
                    <TableCell>عنوان الشيفت</TableCell>
                    <TableCell>اسم الفرع</TableCell>
                    <TableCell>تاريخ الإنشاء</TableCell>
                    <TableCell>تاريخ التحديث</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shiftTitles.map((shift) => (
                    <TableRow key={shift.id}>
                      <TableCell>{shift.id}</TableCell>
                      <TableCell>{shift.title}</TableCell>
                      <TableCell>{shift.root?.name || "غير معروف"}</TableCell>
                      <TableCell>
                        {new Date(shift.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {new Date(shift.updated_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Typography>لا توجد شيفتات متاحة.</Typography>
          )}

          {/* Add Shift Modal */}
          <Dialog
            open={openModal}
            onClose={() => setOpenModal(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>إضافة شيفت جديد</DialogTitle>
            <DialogContent>
              <TextField
                label="اسم الشيفت"
                value={newShiftName}
                onChange={(e) => setNewShiftName(e.target.value)}
                fullWidth
                margin="normal"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenModal(false)}>إلغاء</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddShift}
                disabled={addingShift}
              >
                {addingShift ? "جارٍ الإضافة..." : "إضافة"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Confirm Save Dialog */}
          <Dialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ textAlign: "center" }}>تأكيد الحفظ</DialogTitle>
            <DialogContent sx={{ textAlign: "center" }}>
              <Typography>هل أنت متأكد أنك تريد حفظ حالة الشيفت؟</Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center" }}>
              <Button onClick={() => setConfirmOpen(false)} color="secondary">
                إلغاء
              </Button>
              <Button
                onClick={handleShiftSave}
                color="primary"
                variant="contained"
              >
                تأكيد الحفظ
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={alertOpen}
            onClose={() => setAlertOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ textAlign: "center", color: "red" }}>تنبيه</DialogTitle>
            <DialogContent sx={{ textAlign: "center" }}>
              <Typography>
                لا يمكن أن يعمل الشيفت إجباري واليومية تلقائي.<br />
                برجاء تغيير حالة اليومية إلى "يدوي" أولاً.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center" }}>
              <Button onClick={() => setAlertOpen(false)} color="primary" variant="contained">
                موافق
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Box>
  );
};

export default Shiftname;
